import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X, Loader2, ArrowRightLeft, AlertTriangle } from 'lucide-react';
import { discussionsApi } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { toast } from '@/components/ui/Toaster';
import { cn } from '@/lib/utils';
import { IS_STANDALONE } from '@/config/standalone';

type Mode = 'move' | 'copy';

interface ConvertibleTask {
  id: string;
  title: string;
  dueDate?: string | null;
  startDate?: string | null;
  priority?: string;
  status?: string;
  statusConfig?: { name: string } | null;
  assignee?: { name: string } | null;
  assignees?: Array<{ name: string }>;
  subtasks?: Array<unknown>;
  comments?: Array<unknown>;
  attachments?: Array<unknown>;
  list?: { name: string } | null;
}

/**
 * Project-admin modal: turn a task into a discussion. Clients keep filing
 * discussions as tasks; this puts one where it belongs. The warning lists
 * exactly what this task will lose, computed from the task itself, so the
 * admin is deciding on facts rather than a generic caution.
 */
export function ConvertTaskToDiscussionModal({
  isOpen,
  onClose,
  task,
  onConverted,
}: {
  isOpen: boolean;
  onClose: () => void;
  task: ConvertibleTask;
  onConverted: (discussion: { id: string; discussionType?: string | null }, mode: Mode) => void;
}) {
  const [mode, setMode] = useState<Mode>('move');

  const convertMutation = useMutation({
    mutationFn: () => discussionsApi.convertTask(task.id, { mode }),
    onSuccess: (discussion) => {
      toast({
        type: 'success',
        title: mode === 'move' ? 'Task moved to discussions' : 'Task copied to discussions',
        message: `"${task.title}" is now a discussion.`,
      });
      onConverted(discussion, mode);
    },
    onError: (e: any) => {
      toast({ type: 'error', title: "Couldn't convert the task", message: e?.message });
    },
  });

  if (!isOpen) return null;

  const assigneeNames = (task.assignees?.length ? task.assignees : task.assignee ? [task.assignee] : []).map((a) => a.name);
  const statusName = task.statusConfig?.name ?? task.status;
  const lost: string[] = [];
  if (assigneeNames.length) lost.push(`Assigned to ${assigneeNames.join(', ')}`);
  if (task.dueDate) lost.push(`Due ${new Date(task.dueDate).toLocaleDateString()}`);
  if (task.startDate) lost.push(`Starts ${new Date(task.startDate).toLocaleDateString()}`);
  if (statusName) lost.push(`Status "${statusName}"`);
  if (task.priority) lost.push(`Priority ${task.priority.toLowerCase()}`);
  if (task.list?.name) lost.push(`Its place in the "${task.list.name}" list`);
  if (task.subtasks?.length) lost.push(`${task.subtasks.length} subtask${task.subtasks.length === 1 ? '' : 's'}`);
  const kept: string[] = ['The title and description become the discussion'];
  if (task.comments?.length) kept.push(`${task.comments.length} comment${task.comments.length === 1 ? '' : 's'} become replies, by their original authors`);
  if (task.attachments?.length) kept.push(`${task.attachments.length} attachment${task.attachments.length === 1 ? '' : 's'} come along`);

  const busy = convertMutation.isPending;
  const handleClose = () => { if (!busy) onClose(); };

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center p-4',
        !IS_STANDALONE && 'top-[var(--solid-rail-height,56px)] lg:top-0 lg:left-[var(--solid-rail-width,52px)]',
      )}
    >
      <div className="absolute inset-0 bg-secondary/60 backdrop-blur-sm animate-fade-in" onClick={handleClose} />

      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-elevated animate-scale-in overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-grey-100">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-accent/10">
              <ArrowRightLeft className="w-5 h-5 text-accent" />
            </div>
            <h2 className="text-heading-4 text-secondary">Convert to discussion</h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            aria-label="Close"
            className="p-2 hover:bg-grey-100 rounded-full transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-grey-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">
          <p className="text-sm text-secondary">
            <span className="font-semibold">&ldquo;{task.title}&rdquo;</span> becomes a discussion in this project.
          </p>

          {/* What is lost: a dialog IS the decision point, so this stays on screen. */}
          <div className="rounded-xl border border-warning/30 bg-warning/5 px-4 py-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-secondary">
              <AlertTriangle className="w-4 h-4 text-warning" />
              Discussions have no task features. This is dropped:
            </div>
            <ul className="mt-2 space-y-1 text-sm text-grey-600 list-disc pl-5">
              {lost.length === 0 && <li>Nothing set on this task beyond its title and description.</li>}
              {lost.map((item) => <li key={item}>{item}</li>)}
            </ul>
            <ul className="mt-3 space-y-1 text-sm text-grey-600">
              {kept.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </div>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-secondary mb-2">What happens to the task</legend>
            {([
              ['move', 'Move', 'The task is removed. The discussion is the only copy.'],
              ['copy', 'Copy', 'The task stays as it is, and the two are linked to each other.'],
            ] as const).map(([value, label, help]) => (
              <label
                key={value}
                className={cn(
                  'flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors',
                  mode === value ? 'border-accent bg-accent/5' : 'border-grey-200 hover:bg-grey-50',
                )}
              >
                <input
                  type="radio"
                  name="convert-mode"
                  value={value}
                  checked={mode === value}
                  onChange={() => setMode(value)}
                  disabled={busy}
                  className="mt-1 h-4 w-4 border-grey-300 text-accent focus:ring-accent"
                />
                <span>
                  <span className="block text-sm font-medium text-secondary">{label}</span>
                  <span className="block text-xs text-grey-500">{help}</span>
                </span>
              </label>
            ))}
          </fieldset>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-grey-50 border-t border-grey-100">
          <Button type="button" variant="ghost" onClick={handleClose} disabled={busy} autoFocus>
            Cancel
          </Button>
          <Button type="button" variant={mode === 'move' ? 'danger' : 'primary'} onClick={() => convertMutation.mutate()} disabled={busy}>
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Converting…
              </>
            ) : mode === 'move' ? 'Move to discussions' : 'Copy to discussions'}
          </Button>
        </div>
      </div>
    </div>
  );
}
